import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { EMAIL_VERIFY_BYPASS_SESSION_KEY } from 'constants/auth-session'
import { useStores } from 'hooks/useStores'
import routes from 'routes'

/**
 * Redirect any unverified-email user into the verify-email flow before they
 * touch authed content. Mirrors the gate that lived at the top of legacy
 * `SubDomainRoutes` so flat-host wrappers can compose the same check.
 *
 * Clears the bypass key once the user actually verifies (so a re-login state
 * doesn't silently honor a stale bypass from a previous session).
 *
 * Compose this guard above every authed entry point — including the identity
 * bootstrap pages (/select-organization, /select-profile) — so an unverified
 * user cannot URL-hack past it.
 */
const EmailVerifyGuard = observer(({ children }: { children: JSX.Element }) => {
  const { authStore } = useStores()
  const { userDetail } = authStore
  const hasEmailVerifyBypass = sessionStorage.getItem(EMAIL_VERIFY_BYPASS_SESSION_KEY) === '1'

  useEffect(() => {
    if (userDetail?.isEmailVerified) {
      sessionStorage.removeItem(EMAIL_VERIFY_BYPASS_SESSION_KEY)
    }
  }, [userDetail?.isEmailVerified])

  if (userDetail?.isEmailVerified === false && !hasEmailVerifyBypass) {
    const email = userDetail?.email
    if (email) {
      const params = new URLSearchParams()
      params.set('email', email)
      params.set('send', '1')
      params.set('redirect', routes.selectOrganization.value)
      return <Navigate to={`${routes.verifyEmail.value}?${params.toString()}`} replace />
    }
  }

  return children
})

export default EmailVerifyGuard
